<script setup lang="ts">
import useHasLibrary from '@/composables/use-has-library';
import { computed } from 'vue';
import { useLocale } from '@/plugins/translation';
import useQuizStore from '@/store/quiz';
import { storeToRefs } from 'pinia';
import { ExerciseWithProgress, useExerciseStore } from '@/store/exercises';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

const hasLibrary = useHasLibrary();
const quizStore = useQuizStore();
const { currentQuiz } = storeToRefs(quizStore);
const exercisesStore = useExerciseStore();
const { exercises: storeExercises } = storeToRefs(exercisesStore);
const quizzes = computed<ExerciseWithProgress[]>(() =>
  (storeExercises.value[currentQuiz.value?.resourceId || ''] ?? []).filter((e) => e.type === 'quiz'),
);
const { t, l } = useLocale();

const previous = () => {
  const currentIndex = quizzes.value.findIndex((el) => el.id === currentQuiz.value?.id);

  if (currentIndex !== 0) {
    currentQuiz.value = quizzes.value[currentIndex - 1];
  }
  quizStore.reset();
};
const nextQuiz = () => {
  const currentIndex = quizzes.value.findIndex((el) => el.id === currentQuiz.value?.id);

  if (currentIndex < quizzes.value.length - 1) {
    currentQuiz.value = quizzes.value[currentIndex + 1];
  }
  quizStore.reset();
};
const scrollToTop = () => {
  const supportsNativeSmoothScroll = 'scrollBehavior' in document.documentElement.style;

  if (supportsNativeSmoothScroll) {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  } else {
    window.scrollTo(0, 0);
  }
};
const replayFilm = () => {
  scrollToTop();
};

const hasPreviousQuiz = computed(() => {
  const currentIndex = quizzes.value.findIndex((el) => el.id === currentQuiz.value?.id);
  return currentIndex > 0;
});
const hasNextQuiz = computed(() => {
  const currentIndex = quizzes.value.findIndex((el) => el.id === currentQuiz.value?.id);
  return currentIndex < quizzes.value.length - 1;
});
</script>
<template>
  <div v-if="currentQuiz" class="quiz-navigation-container results-mode">
    <button
      v-if="hasPreviousQuiz"
      class="ma-1 px-1 py-2 px-md-4 py-md-1 align-center navigation-button prev no-flip"
      @click="previous"
    >
      <span class="mr-2">
        <FontAwesomeIcon size="lg" icon="fa-solid fa-chevrons-left" />
      </span>
      {{ t(l.quiz.repeat_first_quiz) }}
    </button>
    <button
      class="ma-1 px-1 py-2 px-md-4 py-md-1 align-center navigation-button prev no-flip"
      @click="() => quizStore.reset()"
    >
      <span class="mr-2">
        <FontAwesomeIcon size="lg" icon="fa-solid fa-rotate-right" />
      </span>
      {{ t(l.quiz.repeat_quiz) }}
    </button>
    <button
      v-if="hasNextQuiz && currentQuiz.progress?.completed"
      class="ma-1 px-1 py-2 px-md-4 py-md-1 align-center navigation-button prev no-flip"
      @click="nextQuiz"
    >
      <span class="mr-2">
        <FontAwesomeIcon size="lg" icon="fa-solid fa-chevrons-right" />
      </span>
      {{ t(l.quiz.next_quiz) }}
    </button>
    <button class="ma-1 px-1 py-2 px-md-4 py-md-1 align-center navigation-button prev no-flip" @click="replayFilm">
      <span class="mr-2">
        <FontAwesomeIcon size="lg" icon="fa-solid fa-play" />
      </span>
      {{ t(l.quiz.replay_film) }}
    </button>
    <button
      v-if="hasLibrary"
      class="ma-1 px-1 py-2 px-md-4 py-md-1 align-center navigation-button next"
      @click="$emit('swap-tab')"
    >
      {{ t(l.quiz.go_to_immerse) }}
      <span class="ml-2">
        <FontAwesomeIcon size="lg" icon="fa-solid fa-arrow-right" />
      </span>
    </button>
  </div>
</template>
<style scoped lang="scss">
@use '@/foundations/theme/settings';
@use 'sass:map';

.quiz-navigation-container {
  display: flex;
  align-items: center;
  justify-content: space-between;

  &.results-mode {
    margin: 2.5rem 0 2rem;
    justify-content: center;

    @media #{map.get(settings.$display-breakpoints, 'md-and-down')} {
      display: block;
    }
  }

  .navigation-button {
    display: flex;
    align-items: center;
    border-radius: 20px;
    height: 48px;
    background: rgb(var(--v-theme-background));
    font-size: 1.125rem;
    line-height: 1.375rem;
    transition: 0.15s ease-out all;
    color: rgb(var(--v-theme-primary));

    @media #{map.get(settings.$display-breakpoints, 'md-and-down')} {
      width: 100%;
      text-align: center;
      justify-content: center;
      padding: 0.625rem 0.625rem;
      margin: 0.625rem 0;
    }

    &.prev {
      span {
        position: relative;
        &:before {
          transform: rotate(180deg);
          margin-top: -2px;
        }
      }
      &.no-flip span:before {
        transform: none;
      }
    }
    &.next {
      span {
        position: relative;
        &:before {
          margin-top: -1px;
        }
      }
    }

    &:hover,
    &.active {
      background-color: rgb(var(--v-theme-primary));
      color: rgb(var(--v-theme-background));
    }
    &.inactive {
      background-color: rgb(var(--v-theme-background));
      color: rgb(var(--v-theme-darkblue));
      pointer-events: none;
      opacity: 0.55;
      &:hover {
        background-color: rgb(var(--v-theme-background));
        color: rgb(var(--v-theme-darkblue));
      }
    }
    span {
      display: flex;
      font-size: 1.375rem;
      line-height: 1.25rem;
    }
  }
}
</style>
